/**
 * BackgroundUploadNotification Component
 *
 * Componente flotante que muestra el progreso de uploads en segundo plano.
 * Aparece en la esquina inferior derecha de la pantalla.
 */

import React from 'react';
import { useBackgroundUpload, BackgroundUpload, UploadStatus } from '@/shared/contexts/BackgroundUploadContext';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (start: Date, end?: Date): string => {
  const ms = (end || new Date()).getTime() - start.getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
};

const getStatusIcon = (status: UploadStatus) => {
  switch (status) {
    case 'uploading':
    case 'processing':
      return <ArrowUpTrayIcon className="h-5 w-5 text-primary-500 animate-pulse" />;
    case 'completed':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    case 'error':
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    default:
      return <FolderIcon className="h-5 w-5 text-gray-400" />;
  }
};

const getStatusText = (upload: BackgroundUpload): string => {
  switch (upload.status) {
    case 'uploading':
      return `Subiendo... ${upload.progress}%`;
    case 'processing':
      return 'Procesando imágenes...';
    case 'completed':
      return `Completado - ${upload.result?.root_files || 0} imágenes`;
    case 'error':
      return upload.error || 'Error desconocido';
    default:
      return 'Pendiente';
  }
};

interface UploadItemProps {
  upload: BackgroundUpload;
  onCancel: () => void;
  onRemove: () => void;
}

const UploadItem: React.FC<UploadItemProps> = ({ upload, onCancel, onRemove }) => {
  const isActive = upload.status === 'uploading' || upload.status === 'processing';

  return (
    <div className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getStatusIcon(upload.status)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {upload.folderName}
            </p>
            <button
              onClick={isActive ? onCancel : onRemove}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
              title={isActive ? 'Cancelar' : 'Eliminar'}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {upload.projectName || 'Proyecto'} • {formatFileSize(upload.fileSize)}
          </p>

          {/* Progress bar for active uploads */}
          {isActive && (
            <div className="mt-2 space-y-1">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    upload.status === 'processing'
                      ? 'bg-primary-500 animate-pulse'
                      : 'bg-primary-500'
                  }`}
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Status text */}
          <p className={`text-xs mt-1 ${
            upload.status === 'error'
              ? 'text-red-500'
              : upload.status === 'completed'
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400'
          }`}>
            {getStatusText(upload)}
            {upload.completedAt && (
              <span className="ml-2 text-gray-400">
                ({formatDuration(upload.startedAt, upload.completedAt)})
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export const BackgroundUploadNotification: React.FC = () => {
  const {
    uploads,
    activeCount,
    hasActiveUploads,
    cancelUpload,
    removeUpload,
    clearCompleted,
    isNotificationPanelOpen,
    setNotificationPanelOpen,
  } = useBackgroundUpload();

  // No mostrar si no hay uploads
  if (uploads.length === 0) {
    return null;
  }

  const completedCount = uploads.filter(u => u.status === 'completed').length;
  const errorCount = uploads.filter(u => u.status === 'error').length;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header - Always visible */}
        <button
          onClick={() => setNotificationPanelOpen(!isNotificationPanelOpen)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <ArrowUpTrayIcon className={`h-5 w-5 ${
                hasActiveUploads ? 'text-primary-500 animate-bounce' : 'text-gray-500'
              }`} />
              {hasActiveUploads && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-primary-500 rounded-full animate-ping" />
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {hasActiveUploads
                  ? `Subiendo ${activeCount} ${activeCount === 1 ? 'carpeta' : 'carpetas'}...`
                  : 'Uploads completados'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {completedCount > 0 && `${completedCount} completados`}
                {completedCount > 0 && errorCount > 0 && ' • '}
                {errorCount > 0 && `${errorCount} con error`}
              </p>
            </div>
          </div>
          {isNotificationPanelOpen ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {/* Expandable content */}
        {isNotificationPanelOpen && (
          <div className="max-h-80 overflow-y-auto">
            {uploads.map(upload => (
              <UploadItem
                key={upload.id}
                upload={upload}
                onCancel={() => cancelUpload(upload.id)}
                onRemove={() => removeUpload(upload.id)}
              />
            ))}

            {/* Clear completed button */}
            {(completedCount > 0 || errorCount > 0) && (
              <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={clearCompleted}
                  className="w-full px-3 py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors flex items-center justify-center gap-2"
                >
                  <TrashIcon className="h-4 w-4" />
                  Limpiar finalizados
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BackgroundUploadNotification;
